import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TableColumn,
  Tooltip,
} from '@ibsys/kit';
import { AlertCircleIcon } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useApplySampleFilter, useSampleConversations, type SampleConversation } from '@/entities/sample';
import { getAttrValueByPath, renderConversationAttrValue } from '@/entities/conversations/model/utils';
import { useSettings } from '@/entities/settings';
import { usePaginatedQuery } from '@/shared/hooks';
import { cn, useAppNavigate } from '@/shared/lib';
import { formatHumanDate } from '@/shared/lib/utils/date';
import { SampleColumnsSettings } from './SampleColumnsSettings';
import { DEFAULT_SAMPLE_COLUMNS } from './sampleColumns';
import { useSampleAttributesSettings } from './useSampleAttributesSettings';

type Props = {
  sampleId?: number;
  isCreating: boolean;
  selectedIds: number[];
  onSelectedIdsChange: (ids: number[]) => void;
  hasPredicate: boolean;
};

export const SampleConversationsTable: FC<Props> = ({
  sampleId,
  isCreating,
  selectedIds,
  onSelectedIdsChange,
  hasPredicate,
}) => {
  const { t, i18n } = useTranslation(['samples', 'common']);
  const navigate = useAppNavigate();
  const { samplesPage: samplesSettings } = useSettings();
  const visibleColumns = samplesSettings?.visibleColumns ?? DEFAULT_SAMPLE_COLUMNS;
  const { selected: selectedAttributes, isLoadingAttributes } = useSampleAttributesSettings();

  const {
    data: { conversations = [], total } = {},
    page,
    changePagination,
    perPage,
    isLoading,
    refetch,
  } = usePaginatedQuery(useSampleConversations, 'conversations', {
    sampleId: sampleId ?? 0,
  });

  const { mutateAsync: applyFilter, isPending: isApplyingFilter } = useApplySampleFilter();

  // Initialize selectedIds from loaded conversations when editing existing sample
  // Only initialize if selectedIds is empty - prevents resetting after navigation
  const isInitializedRef = useRef(false);
  useEffect(() => {
    if (!isCreating && conversations.length > 0 && selectedIds.length === 0 && !isInitializedRef.current) {
      const allIds = conversations.map((c) => c.id);
      onSelectedIdsChange(allIds);
      isInitializedRef.current = true;
    }
  }, [conversations, isCreating, selectedIds.length, onSelectedIdsChange]);

  // Reset initialization flag when sampleId changes
  useEffect(() => {
    isInitializedRef.current = false;
  }, [sampleId]);

  const handleApplyFilter = useCallback(async () => {
    if (!sampleId) return;
    const result = await applyFilter(sampleId);
    if (result.addedCount > 0) {
      refetch();
    }
  }, [sampleId, applyFilter, refetch]);

  const handleSelectAll = useCallback(() => {
    const allIds = conversations.map((c) => c.id);
    onSelectedIdsChange(allIds);
  }, [conversations, onSelectedIdsChange]);

  const handleDeselectAll = useCallback(() => {
    onSelectedIdsChange([]);
  }, [onSelectedIdsChange]);

  const handleRowClick = useCallback(
    (row: SampleConversation) => {
      if (!sampleId) return;
      navigate('/samples/:sampleId/dialog/:dialogId', {
        sampleId: sampleId.toString(),
        dialogId: row.id.toString(),
      });
    },
    [sampleId, navigate],
  );

  const mainColumns: TableColumn<SampleConversation>[] = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
      },
      {
        accessorKey: 'createdAt',
        header: t('samples:conversationDate'),
        size: 150,
        cell: ({ getValue }) => (
          <span className="font-roboto">{formatHumanDate(i18n, getValue<string>(), { withTime: true })}</span>
        ),
      },
      {
        accessorKey: 'employee',
        header: t('samples:employee'),
        size: 150,
        cell: ({ row }) => {
          const employees = row.original.employee;
          if (!employees || employees.length === 0) return '-';
          return employees.map((e) => e.fio).join(', ');
        },
      },
    ],
    [t, i18n],
  );

  const attributeColumns: TableColumn<SampleConversation>[] = useMemo(
    () =>
      selectedAttributes.map((attr) => ({
        accessorKey: attr.path,
        header: () => (
          <span className="flex items-center gap-1">
            {attr.name}
            {attr.pathDescription && (
              <Tooltip tooltip={attr.pathDescription} classNames={{ content: 'max-w-xs' }}>
                <AlertCircleIcon size={14} className="text-muted-foreground cursor-help" aria-hidden="true" />
              </Tooltip>
            )}
          </span>
        ),
        size: 150,
        cell: ({ row }) => {
          const attrValue = getAttrValueByPath(row.original.conversationAttrValues, attr.path);
          return renderConversationAttrValue(attrValue, attr);
        },
      })),
    [selectedAttributes],
  );

  const visibleMainColumns = useMemo(
    () => mainColumns.filter((col) => visibleColumns.includes(col.accessorKey as string)),
    [mainColumns, visibleColumns],
  );

  const visibleAttributeColumns = useMemo(
    () => attributeColumns.filter((col) => visibleColumns.includes(col.accessorKey as string)),
    [attributeColumns, visibleColumns],
  );

  const columns = useMemo(
    () => [...visibleMainColumns, ...visibleAttributeColumns],
    [visibleMainColumns, visibleAttributeColumns],
  );

  if (isCreating) {
    return <Alert title={t('samples:conversationsAfterCreate')} className="text-muted-foreground" />;
  }

  if (isLoading || isLoadingAttributes) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={'overflow-hidden flex flex-col'}>
      <CardHeader className="flex items-center gap-4 border-b">
        <CardTitle className="text-base">{t('samples:conversations')}</CardTitle>
        <span className="text-sm text-muted-foreground">
          {t('samples:selectedCount', { selected: selectedIds.length, total: total ?? 0 })}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {hasPredicate && (
            <Button size="sm" variant="outline" onClick={handleApplyFilter} isLoading={isApplyingFilter}>
              {t('samples:applyFilter')}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={handleSelectAll}>
            {t('samples:selectAll')}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleDeselectAll}>
            {t('samples:deselectAll')}
          </Button>
          <SampleColumnsSettings />
        </div>
      </CardHeader>
      <CardContent className={cn('p-0 overflow-hidden flex-1 flex flex-col', conversations.length === 0 && 'p-4')}>
        {conversations.length === 0 ? (
          <Alert title={t('samples:noConversations')} className="text-muted-foreground" />
        ) : (
          <Table
            data={conversations}
            columns={columns}
            styles={{ size: 'sm' }}
            classNames={{ header: '[&_th]:h-12' }}
            className="border-0"
            selectedKeys={selectedIds}
            onSelect={({ selectedRows }) => {
              onSelectedIdsChange(selectedRows.map((row) => row.id));
            }}
            enableMultiSelect
            pagination={{
              total: total ?? 0,
              page,
              perPage,
            }}
            type={'paginated'}
            onChangePagination={changePagination}
            onClickRow={handleRowClick}
          />
        )}
      </CardContent>
    </Card>
  );
};
